import { ChartData } from "components/dashboard/dataset-window/models/types";
import { dweenColorway } from "utils/constants";
import {
  DatasetLoadingConf,
  IDataSet,
  LagLeadLoadingConfig,
  LineEditorLoadingTypes,
  LoadingConfig,
} from "components/dashboard/charts/interfaces";
import {
  addToDate,
  addToInstant,
  addToZonedDateTime,
  BROWSER_ZONE_ID,
  DweenTimeUnitEnum,
  JodaTimeUtils,
  LT_ZONE_ID,
  toChronoUnit,
  toInstant,
  toInstantOrNow,
  toZonedDateTime,
  truncateDate,
  UTC_ZONE_ID,
} from "utils/time-utils";
import { I, T } from "services/lang/LanguageService";
import { ComponentTheme } from "components/fragments/dashboard/components/group-component/GroupComponentConfiguratorNew";
import { Instant, IsoFields, ZonedDateTime, ZoneId } from "@js-joda/core";
import { DatasetDataInfoResponse } from "api/model/dataset/DatasetDataInfoResponse";
import { Timezones } from "hooks/useTimezone";
import { DatasetStringValueMapperDTO } from "@energy-advice/easas-api";
import { AggregationOption } from "../product-template-bar-chart/product-template-bar-chart-editor/ProductTemplateBarChartTypes";
import { UnifiedMapper } from "./dataset-line-chart/DatasetLineChartTypes";
import { swapDates } from "../product-template-table/utils/timeUtils";
import { AggregatedEntry, ComparisonPeriod } from "../periodic-data-chart/PeriodicDataChartTypes";

export enum TimezoneDisplayLocation {
  SERVER = "server",
  CLIENT = "client",
}

export const colorPallet = {
  key: "Category10",
  value: [
    "#1f77b4",
    "#ff7f0e",
    "#2ca02c",
    "#d62728",
    "#9467bd",
    "#8c564b",
    "#e377c2",
    "#7f7f7f",
    "#bcbd22",
    "#17becf",
    "#393b79",
    "#637939",
    "#8c6d31",
    "#843c39",
    "#7b4173",
    "#a55194",
    "#ce6dbd",
    "#de9ed6",
    "#393b79",
    "#5254a3",
    "#6b6ecf",
    "#9c9ede",
    "#637939",
    "#8ca252",
  ],
};

export const getHexRandomColor = () => {
  const colorsArrayLength = colorPallet.value.length;
  return colorPallet.value[Math.floor(Math.random() * colorsArrayLength)];
};

export const offsetOptions = [
  { label: `${T("time.first-letter.hours")}/2`, value: "HALFHOUR" },
  { label: T("time.first-letter.hours"), value: "HOURS" },
  { label: T("time.first-letter.days"), value: "DAYS" },
  { label: T("time.first-letter.weeks"), value: "WEEKS" },
  { label: T("time.first-letter.months"), value: "MONTHS" },
];

export const hoursArray = Array.from({ length: 24 }, (_, i) => {
  const start = `${i.toString().padStart(2, "0")}:00`;
  const end = `${((i + 1) % 24).toString().padStart(2, "0")}:00`;
  return `${start} - ${end}`;
});

export const daysArray = [
  T("weekday.monday"),
  T("weekday.tuesday"),
  T("weekday.wednesday"),
  T("weekday.thursday"),
  T("weekday.friday"),
  T("weekday.saturday"),
  T("weekday.sunday"),
];

export const daysArrayShort = [
  T("weekday-short.monday"),
  T("weekday-short.tuesday"),
  T("weekday-short.wednesday"),
  T("weekday-short.thursday"),
  T("weekday-short.friday"),
  T("weekday-short.saturday"),
  T("weekday-short.sunday"),
];

export const weeksArray = [`${T("word.week")} 1`, `${T("word.week")} 2`, `${T("word.week")} 3`, `${T("word.week")} 4`];
export const monthsArray = [
  T("month.january"),
  T("month.february"),
  T("month.march"),
  T("month.april"),
  T("month.may"),
  T("month.june"),
  T("month.july"),
  T("month.august"),
  T("month.september"),
  T("month.october"),
  T("month.november"),
  T("month.december"),
];

export const monthsArrayShort = [
  T("month-short.january"),
  T("month-short.february"),
  T("month-short.march"),
  T("month-short.april"),
  T("month-short.may"),
  T("month-short.june"),
  T("month-short.july"),
  T("month-short.august"),
  T("month-short.september"),
  T("month-short.october"),
  T("month-short.november"),
  T("month-short.december"),
];

export const yearsArray = Array.from({ length: 24 }, (_, i) => (2017 + i).toString());

export const initialColorAxisDataset = { data: [], identifier: "", title: "", unit: "" };

export const options = [
  { name: "Range", value: true },
  { name: "Discrete", value: false },
];

export const colorAxisOptions = [
  { name: "Dataset", value: true },
  { name: "Time Offset", value: false },
];

export const isLagLeadLoadingConfig = (conf: LoadingConfig | undefined): conf is LagLeadLoadingConfig => {
  return !conf || conf.type !== LineEditorLoadingTypes.PRODUCT_ENTRY;
};

export function defaultFromDateTime(conf: { loadConf?: DatasetLoadingConf; referenceTime?: Instant; zoneId: ZoneId }) {
  const align = conf.loadConf?.alignUnit;
  const lag = conf.loadConf?.lag ?? 6;
  const lagUnit = conf.loadConf?.lagUnit ?? "HOURS";
  const startingDate = toInstantOrNow(conf.referenceTime);
  const date = truncateDate(startingDate, align, conf.zoneId);

  return addToDate(date, -lag, lagUnit);
}

export function defaultToDateTime(conf: {
  loadConf?: DatasetLoadingConf;
  referenceTime?: Instant;
  minutesToAdd?: number;
  zoneId: ZoneId;
}) {
  if (!conf.zoneId) throw new Error("ZoneId is required");

  const align = conf.loadConf?.alignUnit;
  const lead = conf.loadConf?.lead ?? 1;

  let leadUnit = conf.loadConf?.leadUnit ?? "HOURS";

  if (conf.loadConf?.lead === 0) {
    leadUnit = "SECONDS";
  }

  const startingDate = toInstantOrNow(conf.referenceTime);

  const date = truncateDate(startingDate, align, conf.zoneId);

  return addToDate(addToDate(date, lead, leadUnit), conf.minutesToAdd ?? 1, "MINUTES");
}

export function defaultFromDateTimeInstant(conf: {
  loadConf?: DatasetLoadingConf;
  referenceTime?: Instant;
  zoneId: ZoneId;
}): Instant {
  const align = conf.loadConf?.alignUnit;
  const lag = conf.loadConf?.lag ?? 6;
  const lagUnit = conf.loadConf?.lagUnit ?? DweenTimeUnitEnum.HOURS;
  const startingDate = toInstantOrNow(conf.referenceTime);
  const date = JodaTimeUtils.truncateToZoned(startingDate, align, conf.zoneId);

  return addToZonedDateTime(date, -lag, lagUnit).toInstant();
}

export function defaultToDateTimeInstant(conf: {
  loadConf?: DatasetLoadingConf;
  referenceTime?: Instant;
  minutesToAdd?: number;
  zoneId: ZoneId;
}): Instant {
  if (!conf.zoneId) throw new Error("ZoneId is required");

  const align = conf.loadConf?.alignUnit;
  const lead = conf.loadConf?.lead ?? 1;

  let leadUnit = conf.loadConf?.leadUnit || DweenTimeUnitEnum.HOURS;

  if (conf.loadConf?.lead === 0) {
    leadUnit = DweenTimeUnitEnum.SECONDS;
  }
  const startingDate = toInstantOrNow(conf.referenceTime);

  let date = JodaTimeUtils.truncateToZoned(startingDate, align, conf.zoneId);

  date = addToZonedDateTime(date, lead, leadUnit);
  date = addToZonedDateTime(date, conf.minutesToAdd || 1, DweenTimeUnitEnum.MINUTES);

  return date.toInstant();
}

export const convertValue = (dataArray: Array<any>) => {
  return dataArray?.map(d => {
    return d.value;
  });
};

export const convertKey = (dataArray: Array<any>) => {
  return dataArray.map(d => {
    return new Date(d.key);
  });
};

const getValuesToDisplay = (dates: Date[], timeOffset: string | undefined, colors: string[], tz: Timezones) => {
  if (!timeOffset) {
    return [];
  }

  const uniqueValues = new Set<string>();

  let values = [];
  // FIXME: add tz support
  switch (timeOffset) {
    case DweenTimeUnitEnum.HOURS:
      values = dates.map(date => {
        const hour = date.getHours();
        const start = `${hour.toString().padStart(2, "0")}:00`;
        const end = `${((hour + 1) % 24).toString().padStart(2, "0")}:00`;
        return `${start} - ${end}`;
      });
      break;
    case DweenTimeUnitEnum.DAYS:
      values = dates.map(date => date.toLocaleString("en-US", { weekday: "long" }));
      break;
    case DweenTimeUnitEnum.WEEKS:
      values = dates.map(date => {
        const startOfMonth = new Date(date.getFullYear(), date.getMonth(), 1);
        const weekNumber = Math.ceil((date.getDate() + startOfMonth.getDay()) / 7);
        return `Week ${weekNumber}`;
      });
      break;
    case DweenTimeUnitEnum.MONTHS:
      values = dates.map(date => date.toLocaleString("en-US", { month: "long" }));
      break;
    case DweenTimeUnitEnum.YEARS:
      values = dates.map(date => date.getFullYear().toString());
      break;
    default:
      return [];
  }

  values.forEach(value => uniqueValues.add(value));

  const valueToColor = Array.from(uniqueValues).reduce((acc: Record<string, string>, value: string, index) => {
    acc[value] = colors[index % colors.length];
    return acc;
  }, {});

  return values.map(value => ({
    value,
    color: valueToColor[value],
  }));
};

export const convertDataToChartsData = (
  datasetArray: Array<IDataSet | DatasetDataInfoResponse>,
  scatterSelectedTimeOffset: string | undefined,
  tz: Timezones
): ChartData[] => {
  return datasetArray.map(d => {
    const keyArr = convertKey(d.data);
    const valueArr = convertValue(d.data);

    if (scatterSelectedTimeOffset) {
      const valuesToDisplay = getValuesToDisplay(keyArr, scatterSelectedTimeOffset, colorPallet.value, tz);

      return {
        id: d.identifier,
        title: d.title.replace(/_/g, " "),
        unit: d.unit,
        stringValueMapper: d.stringValueMapper,
        keyArr,
        valueArr,
        decimalPlaces: d.decimalPlaces,
        scatterSelectedTimeOffset,
        valuesToDisplay,
      } as unknown as ChartData;
    }

    return {
      id: d.identifier,
      title: d.title.replace(/_/g, " "),
      unit: d.unit,
      stringValueMapper: d.stringValueMapper,
      keyArr,
      valueArr,
      decimalPlaces: d.decimalPlaces,
    } as ChartData;
  });
};

export const round2decimals = (num: number) => {
  return Math.round((num + Number.EPSILON) * 100) / 100;
};

export const hexToRgb = (hex: string, opacity: number) => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  let colorRGB = null;
  if (result !== null) {
    const r = parseInt(result[1], 16);
    const g = parseInt(result[2], 16);
    const b = parseInt(result[3], 16);
    colorRGB = `rgba(${r},${g},${b},${opacity ?? 0.7})`;
  }
  return colorRGB || null;
};

export const getDweenColor = (index: number, theme?: ComponentTheme) =>
  index === 0 ? getTextColor(theme) : (dweenColorway[index - 1] ?? "#ccc");

type ContentData = {
  date: Date;
  [key: string]: string | Date | number | null;
};

export type TableRowData = {
  columns: { identifier: string; title: string; unit: string }[];
  content: ContentData[];
};

export const getAllDatasetsTimestamps = (
  datasets: Array<IDataSet>,
  showRawValues = false
): {
  content: Array<{ date: Date; [identifier: string]: string | Date | null }>;
  columns: Array<{ identifier: string; title: string; unit: string }>;
} => {
  const tableTemplate: { [key: string]: ContentData } = {};
  const columns = datasets.map(dataset => ({
    identifier: dataset.identifier,
    title: dataset.title.replace(/_/g, " "),
    unit: dataset.unit,
  }));

  datasets.forEach(dataset => {
    dataset.data.forEach(({ key: dateStr, value }) => {
      const date = new Date(dateStr);
      const tempKey = date.toInstant().toString();

      if (!tableTemplate[tempKey]) {
        tableTemplate[tempKey] = { date };
      }

      tableTemplate[tempKey][dataset.identifier] = showRawValues ? value : value.toFixed(dataset.decimalPlaces ?? 2);
    });
  });

  const tableTemplateArray = Object.entries(tableTemplate)
    .map(([key, value]) => ({
      timestamp: key,
      ...value,
    }))
    .sort((a, b) => a.date.getTime() - b.date.getTime());

  return {
    columns,
    content: tableTemplateArray.map(obj => ({ ...obj, timestamp: obj.timestamp })),
  };
};

export const splitLongWord = (word: string) => {
  const chunks = [];
  for (let i = 0; i < word.length; i += 15) {
    chunks.push(word.substring(i, i + 15));
  }
  return chunks.join("-<br>");
};

export const splitStringToLines = (str: string) => {
  const words = str.split(" ");
  const lines = [];
  let currentLine = "";

  words.forEach(word => {
    if (currentLine.length + word.length + 1 <= 15) {
      currentLine += (currentLine ? " " : "") + word;
    } else {
      lines.push(currentLine);
      currentLine = word;
    }
  });

  if (currentLine) {
    lines.push(currentLine);
  }
  return lines;
};

export const shouldShowTime = (cfg?: any): boolean => {
  const unitsToCheck = [
    cfg?.alignUnit,
    cfg?.lagUnit,
    cfg?.leadUnit,
    cfg?.alignStartTo,
    cfg?.startOffsetUnits,
    cfg?.lagUnits,
    cfg?.leadUnits,
    cfg?.stepUnits,
  ];

  if (!cfg) {
    return true;
  }

  return unitsToCheck.some(
    unit => unit === DweenTimeUnitEnum.SECONDS || unit === DweenTimeUnitEnum.MINUTES || unit === DweenTimeUnitEnum.HOURS
  );
};

export const adjustTimeToTz = (from: Date, tzInfo: TimezoneDisplayLocation, forceToLtTz: boolean): Date => {
  if (!forceToLtTz) return new Date(from);

  const instant = Instant.ofEpochMilli(from.getTime());
  let zonedDateTime = ZonedDateTime.ofInstant(instant, UTC_ZONE_ID);

  const userOffsetMinutes = new Date().getTimezoneOffset();
  const vilniusOffsetMinutes = ZonedDateTime.now(LT_ZONE_ID).offset().totalSeconds() / 60;

  const offsetDifferenceMinutes = vilniusOffsetMinutes + userOffsetMinutes;

  const offsetDifferenceHours = offsetDifferenceMinutes / 60;
  if (tzInfo === TimezoneDisplayLocation.SERVER) {
    zonedDateTime = zonedDateTime.minusHours(offsetDifferenceHours);
  } else {
    zonedDateTime = zonedDateTime.plusHours(offsetDifferenceHours);
  }

  return new Date(zonedDateTime.toInstant().toEpochMilli());
};

export const calculateSteps = (
  currentFrom: Instant,
  currentTo: Instant,
  stepSize: number,
  stepUnit: DweenTimeUnitEnum,
  companyTz: ZoneId
): Instant[] => {
  const dateRange = swapDates(currentTo, currentFrom);
  let { from } = dateRange;
  const { to } = dateRange;
  const validStepSize = stepSize > 0 ? stepSize : 1;

  const stepsArr: Array<Instant> = [];

  from = JodaTimeUtils.CeilingTo(from, toChronoUnit(stepUnit), companyTz);
  from = toZonedDateTime(from, companyTz).toInstant();

  while (from.toEpochMilli() <= to.toEpochMilli()) {
    stepsArr.push(toInstant(from));
    from = addToInstant(from, validStepSize, stepUnit, companyTz);
  }
  return stepsArr;
};

export const getMappedLines = (stringVal: string) => {
  const lines = splitStringToLines(stringVal);
  return lines
    .map((currentLine, index) => {
      if (index === 0 || currentLine.length === 15) {
        return currentLine;
      }
      return splitLongWord(currentLine);
    })
    .join("<br>");
};

export const computeUnifiedMapperMappings = (
  datasets: DatasetDataInfoResponse[],
  mappers: DatasetStringValueMapperDTO[]
): Record<string, UnifiedMapper> => {
  const result: Record<string, UnifiedMapper> = {};
  const usedValuesPerMapper: Record<number, Set<number>> = {};

  datasets.forEach(ds => {
    const mapperId = ds.stringValueMapper;
    if (!mapperId) return;

    if (!usedValuesPerMapper[mapperId]) {
      usedValuesPerMapper[mapperId] = new Set<number>();
    }

    ds.data.forEach(d => {
      if (typeof d.value === "number") {
        usedValuesPerMapper[mapperId].add(d.value);
      }
    });

    const mapper = mappers.find(m => Number(m.id) === mapperId);
    if (!mapper) return;

    if (!result[mapperId]) {
      result[mapperId] = {
        values: {},
        tickvals: [],
        ticktext: [],
        texts: [],
        title: mapper.title,
      };
    }

    const usedValues = usedValuesPerMapper[mapperId];
    Object.entries(mapper.mappings).forEach(([rawKey, rawValue]) => {
      const key = Number(rawKey);
      if (usedValues.has(key) && !(key in result[mapperId].values)) {
        result[mapperId].tickvals.push(key);
        result[mapperId].texts.push([rawKey, String(rawValue)]);
      }
    });
  });

  Object.keys(result).forEach(mapperId => {
    const entry = result[mapperId];
    entry.tickvals = [...new Set(entry.tickvals)].sort((a, b) => a - b);
    entry.values = {};
    entry.tickvals.forEach((val, index) => {
      entry.values[val] = index;
    });
    entry.ticktext = entry.tickvals.map(val => getMappedLines(I(mappers.find(m => m.id === mapperId)?.mappings[val] ?? "")));
  });
  return result;
};

export const getTextColor = (theme?: ComponentTheme) => {
  if (theme === ComponentTheme.DARK) {
    return "white";
  }
  return "black";
};

export const getLegendGroup = (instant: Instant, comparisonPeriod: string, zoneId: ZoneId): string => {
  const date = instant.atZone(zoneId);

  switch (comparisonPeriod) {
    case ComparisonPeriod.HOURLY_DAILY:
      return date.toLocalDate().toString();
    case ComparisonPeriod.DAILY_WEEKLY:
      return `W${getWeekNumber(date)}-${date.year()}`;
    case ComparisonPeriod.WEEKLY_MONTHLY:
      return `${monthsArrayShort[date.monthValue() - 1]} ${date.year()}`;
    case ComparisonPeriod.MONTHLY_YEARLY:
    case ComparisonPeriod.WEEKLY_YEARLY:
    default:
      return date.year().toString();
  }
};

const getWeekNumber = (date: ZonedDateTime): number => date.get(IsoFields.WEEK_OF_WEEK_BASED_YEAR);

const weeksInYear = (zdt: ZonedDateTime): number => zdt.withMonth(12).withDayOfMonth(28).get(IsoFields.WEEK_OF_WEEK_BASED_YEAR);

export const getLastFinishedWeek = (
  refWeek: number,
  refYear: number,
  refZdt: ZonedDateTime
): { refWeek: number; refYear: number } => {
  refWeek -= 1;
  if (refWeek < 1) {
    refYear -= 1;
    const prevYearWeeks = weeksInYear(refZdt.minusYears(1));
    refWeek = prevYearWeeks;
  }
  return { refWeek, refYear };
};

export const getWeekRange = (
  fromOffset: number,
  toOffset: number,
  zone: ZoneId,
  useLastFinishedWeek = false
): { week: number; year: number }[] => {
  const refZdt = ZonedDateTime.now(zone);
  let refWeek = refZdt.get(IsoFields.WEEK_OF_WEEK_BASED_YEAR);
  let refYear = refZdt.year();

  if (useLastFinishedWeek) {
    refWeek = getLastFinishedWeek(refWeek, refYear, refZdt).refWeek;
    refYear = getLastFinishedWeek(refWeek, refYear, refZdt).refYear;
  }

  const totalWeeks = weeksInYear(refZdt);
  const result: { week: number; year: number }[] = [];

  for (let i = fromOffset; i <= toOffset; i++) {
    let week = refWeek + i;
    let year = refYear;

    if (week < 1) {
      year -= 1;
      const prevYearWeeks = weeksInYear(refZdt.minusYears(1));
      week = prevYearWeeks + week;
    } else if (week > totalWeeks) {
      year += 1;
      week -= totalWeeks;
    }
    result.push({ week, year });
  }
  return result;
};

export const getXAxisLabel = (instant: Instant, comparisonPeriod: string, zoneId: ZoneId): string => {
  const date = instant.atZone(zoneId);

  switch (comparisonPeriod) {
    case ComparisonPeriod.HOURLY_DAILY:
      return `${date.hour().toString().padStart(2, "0")}:00`;
    case ComparisonPeriod.DAILY_WEEKLY:
      return date.toLocalDate().toString();
    case ComparisonPeriod.WEEKLY_YEARLY:
      return `W${getWeekNumber(date)}`;
    case ComparisonPeriod.WEEKLY_MONTHLY:
      return `${T("word.week")} ${getWeekOfMonth(date)}`;
    case ComparisonPeriod.MONTHLY_YEARLY:
    default: {
      const monthIndex = date.monthValue() - 1;
      return monthsArrayShort[monthIndex];
    }
  }
};

const getWeekOfMonth = (date: ZonedDateTime): number => {
  const firstDay = date.withDayOfMonth(1);
  const dayOfWeek = firstDay.dayOfWeek().value();
  const day = date.dayOfMonth();
  return Math.ceil((day + dayOfWeek - 1) / 7);
};

export const aggregateValues = (entry: AggregatedEntry, type: AggregationOption) => {
  switch (type) {
    case AggregationOption.AVG:
      return entry.total / entry.count;
    case AggregationOption.MIN:
      return entry.min;
    case AggregationOption.MAX:
      return entry.max;
    case AggregationOption.COUNT:
      return entry.count;
    case AggregationOption.SUM:
    default:
      return entry.total;
  }
};

export const formatNumber = (value: number | string, decimalPlaces = 0, convertToAbsoluteValues = false): string => {
  if (!Number.isFinite(value)) return "–";

  const numericValue = Number(value);

  const en = new Intl.NumberFormat("en-US", {
    minimumFractionDigits: decimalPlaces,
    maximumFractionDigits: decimalPlaces,
    useGrouping: true,
  }).format(convertToAbsoluteValues ? Math.abs(numericValue) : numericValue);

  return en.replace(/,/g, " ");
};

export function getJsonDiff(o1: any, o2: any) {
  const diff: any = {};
  let tmp = null;
  if (JSON.stringify(o1) === JSON.stringify(o2)) return;

  for (const k in o1) {
    if (Array.isArray(o1[k]) && Array.isArray(o2[k])) {
      tmp = o1[k].reduce(function (p, c, i) {
        const _t = getJsonDiff(c, o2[k][i]);
        if (_t) p.push(_t);
        return p;
      }, []);
      if (Object.keys(tmp).length > 0) diff[k] = tmp;
    } else if (typeof o1[k] === "object" && typeof o2[k] === "object") {
      tmp = getJsonDiff(o1[k], o2[k]);
      if (tmp && !!Object.keys(tmp)) diff[k] = tmp;
    } else if (o1[k] !== o2[k]) {
      diff[k] = o2[k];
    }
  }
  return diff;
}
